import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from '@/shared/hooks/useDebounce';
import { getDataGridLocalization } from '@/features/studies/constants/dataGridLocalization';
import {
  Box,
  Container,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  Button,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import {
  Clear as ClearIcon,
  Visibility as ViewIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useGetAuditLogs } from '@/features/audit-logs/api';
import { AuditLogDto } from '@aphp-ema/common/audit';
import { Dayjs } from 'dayjs';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { AuditTrail } from '../components/AuditTrail';
import {
  formatTimestamp,
  getActionChipColor,
  formatResourceId,
  formatIpAddress,
  formatDetailsForDisplay,
  formatDetailsForTooltip,
  buildResourceHistoryUrl,
} from '../utils/list.utils';

const auditActions = [
  'CREATE_STUDY',
  'UPDATE_STUDY',
  'DELETE_STUDY',
  'PUBLISH_STUDY',
  'UNPUBLISH_STUDY',
  'CREATE_STUDY_CATEGORY',
  'UPDATE_INITIAL_QUESTIONNAIRE',
  'UPDATE_EMA_QUESTIONNAIRE',
  'UPDATE_STUDY_SCHEDULE',
  'UPDATE_STUDY_CONSENT',
  'UPDATE_STUDY_QUESTIONS',
] as const;

const resourceTypes = [
  'STUDY',
  'USER',
  'QUESTIONNAIRE',
  'INITIAL_QUESTIONNAIRE',
  'EMA_QUESTIONNAIRE',
  'STUDY_CATEGORY',
] as const;

export const AuditLogsList: React.FC = () => {
  const { t } = useTranslation();
  const localeText = getDataGridLocalization(t);
  const [urlSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const resourceIdParam = urlSearchParams.get('resourceId');

  // Client-side search parameters
  const [searchParams, setSearchParams] = useState({
    userEmail: '',
    resourceId: '',
    resourceType: '',
    action: '',
  });

  const [dateFrom, setDateFrom] = useState<Dayjs | null>(null);
  const [dateTo, setDateTo] = useState<Dayjs | null>(null);

  // Debounce search parameters to prevent excessive filtering operations
  const debouncedSearchParams = useDebounce(searchParams, 300);

  // Load audit logs with maximum allowed limit for client-side filtering
  const { data, isLoading, error } = useGetAuditLogs(1, 100);

  useEffect(() => {
    if (resourceIdParam) {
      setSearchParams((prev) => ({
        ...prev,
        resourceId: resourceIdParam,
      }));
    } else {
      // Clear the resourceId filter when navigating back
      setSearchParams((prev) => ({
        ...prev,
        resourceId: '',
      }));
    }
  }, [resourceIdParam]);

  // Handle search input changes
  const handleSearchChange = (field: string, value: string) => {
    setSearchParams((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleDateFromChange = (newValue: Dayjs | null) => {
    setDateFrom(newValue);
  };

  const handleDateToChange = (newValue: Dayjs | null) => {
    setDateTo(newValue);
  };

  const clearFilters = () => {
    setSearchParams({
      userEmail: '',
      resourceId: '',
      resourceType: '',
      action: '',
    });
    setDateFrom(null);
    setDateTo(null);
  };

  // Check if any search parameters are active
  const hasActiveSearch =
    Object.values(searchParams).some((value) => value !== '') ||
    dateFrom !== null ||
    dateTo !== null;

  // Client-side filtering logic
  const filteredRows = useMemo(() => {
    if (!data?.data) return [];

    return data.data.filter((row) => {
      // If no search parameters are active, return all rows
      if (!hasActiveSearch) return true;

      // Check userEmail (fuzzy matching)
      if (
        debouncedSearchParams.userEmail &&
        (!row.userEmail ||
          !row.userEmail
            .toLowerCase()
            .includes(debouncedSearchParams.userEmail.toLowerCase()))
      ) {
        return false;
      }

      // Check resourceId (fuzzy matching)
      if (
        debouncedSearchParams.resourceId &&
        (!row.resourceId ||
          !row.resourceId
            .toLowerCase()
            .includes(debouncedSearchParams.resourceId.toLowerCase()))
      ) {
        return false;
      }

      // Check resourceType (fuzzy matching)
      if (
        debouncedSearchParams.resourceType &&
        (!row.resourceType ||
          !row.resourceType
            .toLowerCase()
            .includes(debouncedSearchParams.resourceType.toLowerCase()))
      ) {
        return false;
      }

      // Check action (fuzzy matching)
      if (
        debouncedSearchParams.action &&
        (!row.action ||
          !row.action
            .toLowerCase()
            .includes(debouncedSearchParams.action.toLowerCase()))
      ) {
        return false;
      }

      // Check date range
      if (dateFrom && new Date(row.timestamp) < dateFrom.toDate()) {
        return false;
      }

      if (dateTo && new Date(row.timestamp) > dateTo.toDate()) {
        return false;
      }

      // If all checks pass, include the row
      return true;
    });
  }, [data?.data, debouncedSearchParams, dateFrom, dateTo, hasActiveSearch]);

  const columns: GridColDef[] = [
    {
      field: 'timestamp',
      headerName: t('pages.auditLogs.fields.dateTime'),
      width: 180,
      valueFormatter: (value) => {
        return formatTimestamp(value);
      },
    },
    {
      field: 'action',
      headerName: t('pages.auditLogs.fields.action'),
      width: 200,
      renderCell: (params) => (
        <Chip
          label={t(`pages.auditLogs.actions.${params.value}`, params.value)}
          size="small"
          variant="outlined"
          color={getActionChipColor(params.value)}
        />
      ),
    },
    {
      field: 'resourceType',
      headerName: t('pages.auditLogs.fields.resourceType'),
      width: 150,
      renderCell: (params) =>
        t(`pages.auditLogs.resourceTypes.${params.value}`, params.value),
    },
    {
      field: 'resourceId',
      headerName: t('pages.auditLogs.fields.resourceId'),
      width: 250,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            variant="body2"
            sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
          >
            {formatResourceId(params.value)}
          </Typography>
          {params.value && (
            <Tooltip title={t('pages.auditLogs.tooltips.viewResourceHistory')}>
              <IconButton
                size="small"
                onClick={() => navigate(buildResourceHistoryUrl(params.value))}
              >
                <ViewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
    {
      field: 'userEmail',
      headerName: t('pages.auditLogs.fields.user'),
      width: 200,
    },
    {
      field: 'ipAddress',
      headerName: t('pages.auditLogs.fields.ipAddress'),
      width: 120,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
        >
          {formatIpAddress(params.value)}
        </Typography>
      ),
    },
    {
      field: 'details',
      headerName: t('pages.auditLogs.fields.details'),
      width: 200,
      renderCell: (params) => (
        <Tooltip title={formatDetailsForTooltip(params.value)}>
          <Typography
            variant="body2"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '100%',
            }}
          >
            {formatDetailsForDisplay(params.value)}
          </Typography>
        </Tooltip>
      ),
    },
  ];

  // Remove this - we now use hasActiveSearch directly

  if (error) {
    return (
      <Container maxWidth="xl">
        <Paper sx={{ p: 3, mt: 2 }}>
          <Typography color="error">
            {t('pages.auditLogs.messages.loadError')}: {error.message}
          </Typography>
        </Paper>
      </Container>
    );
  }

  // If we have a resourceId in the URL, show the audit trail for that resource
  if (resourceIdParam) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ py: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/audit-logs')}
              variant="outlined"
            >
              {t('pages.auditLogs.list.backToList')}
            </Button>
            <Typography variant="h4">
              {t('pages.auditLogs.list.resourceHistory')}
            </Typography>
          </Box>
          <AuditTrail resourceId={resourceIdParam} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 3 }}>
        <Typography variant="h4" gutterBottom>
          {t('pages.auditLogs.list.title')}
        </Typography>

        {/* Filters */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography variant="h6">
              {t('pages.auditLogs.filters.title')}
            </Typography>
            {hasActiveSearch && (
              <IconButton onClick={clearFilters} size="small">
                <ClearIcon />
              </IconButton>
            )}
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label={t('pages.auditLogs.filters.userEmail')}
                value={searchParams.userEmail || ''}
                onChange={(e) =>
                  handleSearchChange('userEmail', e.target.value)
                }
                size="small"
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label={t('pages.auditLogs.filters.resourceId')}
                value={searchParams.resourceId || ''}
                onChange={(e) =>
                  handleSearchChange('resourceId', e.target.value)
                }
                size="small"
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>
                  {t('pages.auditLogs.filters.resourceType')}
                </InputLabel>
                <Select
                  value={searchParams.resourceType || ''}
                  onChange={(e) =>
                    handleSearchChange('resourceType', e.target.value)
                  }
                  label={t('pages.auditLogs.filters.resourceType')}
                >
                  <MenuItem value="">
                    {t('pages.auditLogs.filters.all')}
                  </MenuItem>
                  {resourceTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {t(`pages.auditLogs.resourceTypes.${type}`, type)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('pages.auditLogs.filters.action')}</InputLabel>
                <Select
                  value={searchParams.action || ''}
                  onChange={(e) => handleSearchChange('action', e.target.value)}
                  label={t('pages.auditLogs.filters.action')}
                >
                  <MenuItem value="">
                    {t('pages.auditLogs.filters.allActions')}
                  </MenuItem>
                  {auditActions.map((action) => (
                    <MenuItem key={action} value={action}>
                      {t(`pages.auditLogs.actions.${action}`, action)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <DateTimePicker
                label={t('pages.auditLogs.filters.dateFrom')}
                value={dateFrom}
                onChange={handleDateFromChange}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <DateTimePicker
                label={t('pages.auditLogs.filters.dateTo')}
                value={dateTo}
                onChange={handleDateToChange}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* No results message */}
        {hasActiveSearch && filteredRows.length === 0 && !isLoading && (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body1">
              {t(
                'pages.auditLogs.search.noResults',
                'No audit logs found matching your search criteria.',
              )}
            </Typography>
          </Box>
        )}

        {/* Data Grid */}
        <Paper sx={{ height: 600, width: '100%' }}>
          <DataGrid
            rows={filteredRows}
            columns={columns}
            loading={isLoading}
            pagination
            pageSizeOptions={[10, 25, 50, 100]}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 25 },
              },
            }}
            disableRowSelectionOnClick
            getRowId={(row: AuditLogDto) => row.id}
            localeText={localeText}
            sx={{
              '& .MuiDataGrid-cell': {
                border: 'none',
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          />
        </Paper>
      </Box>
    </Container>
  );
};
